import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import TableGrid from './TableGrid';
import TableActionModal from './TableActionModal';
import AdminPanel from './AdminPanel';
import { UserRole } from '../types';
import { audioService } from '../services/audioService';

interface AppLayoutProps {
  onServed?: () => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({ onServed }) => {
  const { userRole, setUserRole, isLoading, error } = useApp();
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);

  const handleTableClick = (tableId: number) => {
    setSelectedTableId(tableId);
  };

  const closeModal = () => {
    setSelectedTableId(null);
  };

  const toggleRole = () => {
    const newRole = userRole === UserRole.KITCHEN ? UserRole.PIZZA : UserRole.KITCHEN;
    setUserRole(newRole);
    
    // Update URL
    const url = new URL(window.location.href);
    url.searchParams.set('role', newRole);
    window.history.pushState({}, '', url.toString());
  };

  const toggleSound = () => {
    const newSoundState = !soundEnabled;
    setSoundEnabled(newSoundState);
    audioService.setEnabled(newSoundState);
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const getRoleDisplayName = (role: UserRole): string => {
    return role === UserRole.KITCHEN ? 'Küche' : 'Pizzaiolo';
  };

  const getRoleIcon = (role: UserRole): string => {
    return role === UserRole.KITCHEN ? '👨‍🍳 ' : '🍕 ';
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <div className="app-title">
          <span>🍽️</span>
          <span>Tisch Koordination</span>
        </div>
        
        <div className="header-actions">
          {error && (
            <div className="error-message">
              <span>❌</span>
              <div>
                <strong>Fehler:</strong> {error}
                {error.includes('Azure Storage') && (
                  <div style={{ fontSize: '0.8em', marginTop: '4px' }}>
                    Tipp: Kopieren Sie .env.template zu .env.local und konfigurieren Sie Ihre Azure Storage Credentials.
                  </div>
                )}
              </div>
            </div>
          )}
          
          {isLoading && (
            <div className="loading-indicator">
              <span>⏳</span>
              <span>Synchronisiere mit Azure Storage...</span>
            </div>
          )}
          
          <div className={`role-indicator role-${userRole}`}>
            <span>{getRoleIcon(userRole)}</span>
            <span>{getRoleDisplayName(userRole)}</span>
          </div>
          
          <button 
            className="header-button"
            onClick={toggleSound}
            title={soundEnabled ? "Sound deaktivieren" : "Sound aktivieren"}
          >
            <span>{soundEnabled ? '🔊' : '🔇'}</span>
          </button>
          
          <button 
            className="header-button"
            onClick={() => setShowAdmin(!showAdmin)}
            title="Einstellungen"
          >
            <span>⚙️</span>
            <span>Einstellungen</span>
          </button>

          {error && (
            <button 
              className="header-button"
              onClick={handleRefresh}
              title="App neu laden"
              style={{ background: '#e74c3c' }}
            >
              <span>🔄</span>
              <span>Neu laden</span>
            </button>
          )}
        </div>
      </header>

      {/* Admin Panel (ausklappbar) */}
      {showAdmin && (
        <AdminPanel 
          onClose={() => setShowAdmin(false)}
          userRole={userRole}
          onToggleRole={toggleRole}
          getRoleDisplayName={getRoleDisplayName}
          getRoleIcon={getRoleIcon}
        />
      )}

      {/* Main Content */}
      <main className="main-content">
        <TableGrid onTableClick={handleTableClick} />
      </main>

      {/* Table Action Modal */}
      {selectedTableId !== null && (
        <TableActionModal
          tableId={selectedTableId}
          onClose={closeModal}
          onServed={onServed}
        />
      )}
    </div>
  );
};

export default AppLayout;